import { observer } from 'mobx-react';
import { FC, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { FieldValues } from 'react-hook-form/dist/types';
import { useTranslation } from 'react-i18next';

import useModal from '@yaydoo/react-components/lib/hooks/useModal';
import useToast from '@yaydoo/react-components/lib/hooks/useToast';

import { IContinueButton } from '@components/modals/Redirect3dSecure';
import PayButton from '@components/PayButton';
import GenericCardFields from '@containers/checkout/payment-form/CardPaymentForm/GenericCardFields';
import { PartnerFormProps } from '@containers/checkout/payment-form/types';
import usePopUpWindow from '@hooks/usePopupWindow';
import useStore from '@hooks/useStore';
import { FormPayload } from '@store/fiserv';
import getUrlParam, { getProcessingUrl } from '@utils/getUrlParam';

import AuthorizedHandler from './AuthorizedHandler';
import IframeHandler from './IframeHandler';
import PopupWindowHandler from './PopupWindowHandler';

const FiservCardForm: FC<PartnerFormProps> = ({ onValidationError }) => {
  const { handleSubmit, getValues } = useFormContext();
  const { t } = useTranslation();
  const { openToast } = useToast();
  const { showModal, closeModal } = useModal();
  const { openPopupWindow } = usePopUpWindow();
  const [disabledContinueButton, setDisabledContinueButton] = useState(true);
  const { fiserv, links, checkout } = useStore();
  const { iframeCode, approvedTransaction } = fiserv;
  const acsURLParam = getUrlParam('acsURL');
  const cReqURLParam = getUrlParam('cReq');
  const authorizedURLParam = getUrlParam('authorization');

  const handleCommonClose = () => {
    closeModal();
    fiserv.restoreValues();
  };

  const handleNotifyUpdatePaymentError = async () => {
    if (fiserv.paymentIntentId) {
      try {
        await fiserv.updatePayment(false);
      } catch (error) {
        handleError(error as { message: string });
      }
    }
  };

  const handleCloseModal = () => {
    // manual close of modal
    handleCommonClose();
    handleNotifyUpdatePaymentError();
  };
  const handleClosePopupWindow = () => {
    handleCommonClose();
    handleFinishProcess();
  };
  const handleOpenPopupWindow = () => {
    const urlBase = getProcessingUrl();
    const urlFormatted = `${urlBase}?acsURL=${fiserv.acsURL}&cReq=${fiserv.cReq}`;
    openPopupWindow(urlFormatted, handleClosePopupWindow);
    setDisabledContinueButton(true);
  };

  const handleFinishProcess = async () => {
    const formValues = getValues() as FormPayload;
    try {
      const success = await fiserv.finishProcess({
        ...formValues,
        paymentLinkId: links.paymentLink.paymentLinkId,
      });

      if (success) {
        links.setAsPaid();
      } else {
        handleError({ message: '' });
      }
    } catch (error) {
      handleError(error as { message: string });
      handleNotifyUpdatePaymentError();
    }
  };

  const continueButtonProps: IContinueButton = useMemo(
    () => ({ onClick: handleOpenPopupWindow, disabled: disabledContinueButton }),
    [disabledContinueButton],
  );

  const handleError = (error: { message: string }) => {
    const { message = '' } = error;
    openToast({
      type: 'error',
      title: t('checkoutScreen.payment.errors.card.title'),
      message: message || t('checkoutScreen.payment.errors.card.unknown'),
    });
  };

  useEffect(() => {
    if (approvedTransaction) {
      handleFinishProcess();
    }
  }, [approvedTransaction]);

  const payContinue = async () => {
    const formValues = getValues() as FormPayload;
    try {
      const form = await fiserv.payContinue({
        ...formValues,
        paymentLinkId: links.paymentLink.paymentLinkId,
      });
      if (form) {
        // enable continue button to be able to open PopupWindow
        fiserv.setIsSendingPayment(true);
        setDisabledContinueButton(false);
      } else {
        handleCommonClose();
      }
    } catch (error) {
      handleError(error as { message: string });
      handleNotifyUpdatePaymentError();
    }
  };

  const onSubmit = async (params: FormPayload) => {
    try {
      await fiserv.pay({ ...params, paymentLinkId: links.paymentLink.paymentLinkId });
    } catch (error) {
      handleError(error as { message: string });
      handleNotifyUpdatePaymentError();
    }
  };

  if (authorizedURLParam) {
    return <AuthorizedHandler authorizedURLParam={authorizedURLParam} />;
  }

  if (acsURLParam && cReqURLParam) {
    return <PopupWindowHandler acsURL={acsURLParam} cReq={cReqURLParam} />;
  }

  return (
    <>
      <GenericCardFields />
      <PayButton
        onClick={handleSubmit(onSubmit as SubmitHandler<FieldValues>, onValidationError)}
        disabled={fiserv.isProcessing || !checkout.isAllowedToPayWithCard}
        loading={fiserv.isProcessing}
        label={t('checkoutScreen.payment.methods.submit')}
      />
      {fiserv.paymentIntentId && (iframeCode || fiserv.acsURL) && (
        <IframeHandler
          iframeCode={iframeCode}
          callback={payContinue}
          showModal={showModal}
          closeModal={handleCloseModal}
          continueButton={continueButtonProps}
          acsURL={fiserv.acsURL}
        />
      )}
    </>
  );
};

export default observer(FiservCardForm);
